import { useRef, useState, useEffect } from "react";
import { uploadReference, deleteReferenceFile } from "../lib/references/uploadReference.js";
import { compressImage } from "../lib/images/compressImage.js";
import { LIMITS, canEdit } from "../constants.js";
import { TrashIcon, UploadIcon } from "../Components/Icons";
import "./ReferencesTab.css";

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const KINDS = [
  { value: "logo", label: "Logo" },
  { value: "product", label: "Product" },
  { value: "example", label: "Example" },
  { value: "font", label: "Font (woff2)" },
];

// Soft/hard warning thresholds for an individual reference's own file
// size - separate from LIMITS.MAX_UPLOAD_MB (the hard upload-rejection
// ceiling). These are advisory only, shown as a badge; nothing here
// blocks upload or use.
const SIZE_WARN_SOFT_BYTES = 200 * 1024;
const SIZE_WARN_HARD_BYTES = 500 * 1024;

function referenceSizeLevel(sizeBytes) {
  if (!Number.isFinite(sizeBytes)) return null;
  if (sizeBytes > SIZE_WARN_HARD_BYTES) return "hard";
  if (sizeBytes > SIZE_WARN_SOFT_BYTES) return "soft";
  return "ok";
}

export default function ReferencesTab({ config, brand, references, onSaveReferences }) {
  const [kind, setKind] = useState("logo");
  const [label, setLabel] = useState("");
  const [filter, setFilter] = useState("all");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadApiResponse, setUploadApiResponse] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [compressedFile, setCompressedFile] = useState(null);
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressError, setCompressError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);
  const editable = canEdit(config.userRole);

  const activeFile = compressedFile ?? pendingFile;
  const canCompress = kind !== "logo" && kind !== "font" && !compressedFile;

  useEffect(() => {
    if (!pendingFile || kind === "font") {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(pendingFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingFile, kind]);

  const items = references ?? [];
  const atLimit = items.length >= LIMITS.MAX_REFERENCE_IMAGES_PER_BRAND;
  const visible = filter === "all" ? items : items.filter((item) => item.kind === filter);

  function handleFileSelected(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setUploadError(null);

    if (kind === "font") {
      if (!file.name.toLowerCase().endsWith(".woff2")) {
        setUploadError("Brand fonts must be .woff2 files.");
        return;
      }
    } else if (!file.type.startsWith("image/")) {
      setUploadError("Only image files are supported (PNG, JPG, WebP, SVG, GIF).");
      return;
    }
    if (file.size > LIMITS.MAX_UPLOAD_MB * 1024 * 1024) {
      setUploadError(
        `That file is ${(file.size / 1024 / 1024).toFixed(1)} MB — the limit is ${LIMITS.MAX_UPLOAD_MB} MB.`
      );
      return;
    }

    setPendingFile(file);
    setCompressedFile(null);
    setCompressError(null);
  }

  async function handleCompress() {
    setIsCompressing(true);
    setCompressError(null);
    try {
      const result = await compressImage(pendingFile);
      if (result.size < pendingFile.size) {
        setCompressedFile(result);
      } else {
        // Confirmed via testing: simple/flat-color images can grow under
        // canvas re-encoding - only ever offer the compressed version
        // when it's genuinely smaller, never let it silently replace a
        // better original.
        setCompressError(
          "Compression didn't reduce the size for this image — this can happen with detailed, " +
            "semi-transparent images, since PNG re-encoding has no way to trade quality for size the way JPEG does."
        );
      }
    } catch (err) {
      setCompressError(err.message || "Couldn't compress this image.");
    } finally {
      setIsCompressing(false);
    }
  }

  function handleCancelPending() {
    setPendingFile(null);
    setCompressedFile(null);
    setCompressError(null);
  }

  async function handleConfirmUpload() {
    setUploadError(null);
    try {
      const { referenceId, key, url, sizeBytes } = await uploadReference(
        config,
        { brandId: brand.id, kind, file: activeFile, currentReferenceCount: (references ?? []).length },
        setIsUploading,
        setUploadApiResponse
      );

      const newReference = {
        id: referenceId,
        kind,
        label: label.trim() || null,
        url,
        key,
        sizeBytes,
        createdAt: new Date().toISOString(),
      };

      await onSaveReferences(brand.id, [...items, newReference]);
      setLabel("");
      setPendingFile(null);
      setCompressedFile(null);
      setCompressError(null);
    } catch (err) {
      setUploadError("Upload failed. Please try again.");
    }
  }

  async function handleDelete(item) {
    setDeleteError(null);
    setPendingDeleteId(item.id);
    try {
      await deleteReferenceFile(config, { brandId: brand.id, key: item.key }, () => {}, () => {});
      await onSaveReferences(
        brand.id,
        items.filter((r) => r.id !== item.id)
      );
    } catch (err) {
      setDeleteError("Couldn't delete this reference. Please try again.");
    } finally {
      setPendingDeleteId(null);
    }
  }

  return (
    <div className="references-tab">
      {/* Upload */}
      <div className="references-tab__upload">
        <div className="references-tab__upload-header">
          <h2 className="references-tab__upload-title">Upload a reference</h2>
          <span className={`references-tab__count${atLimit ? " references-tab__count--limit" : ""}`}>
            {items.length} / {LIMITS.MAX_REFERENCE_IMAGES_PER_BRAND}
          </span>
        </div>
        <p className="references-tab__upload-desc">
          Images up to {LIMITS.MAX_UPLOAD_MB} MB. Logos also show on the brand card.
        </p>

        {!editable ? (
          <p className="references-tab__role-notice">Editors and admins only.</p>
        ) : uploadError ? (
          <p className="references-tab__error">{uploadError}</p>
        ) : null}

        <div className="references-tab__upload-row">
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            disabled={atLimit || !editable || !!pendingFile}
          >
            {KINDS.map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </select>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Label (optional)"
            maxLength={80}
            disabled={atLimit || !editable}
            className="references-tab__label-input"
          />
          {!pendingFile ? (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={atLimit || isUploading || !editable}
              className="btn btn--primary"
            >
              <UploadIcon className="references-tab__icon" />
              Upload reference
            </button>
          ) : null}
        </div>

        {pendingFile ? (
          <div className="references-tab__preview">
            {previewUrl ? (
              <img src={previewUrl} alt="" className="references-tab__preview-thumb" />
            ) : null}
            <div className="references-tab__preview-info">
              <span className="references-tab__preview-name">{pendingFile.name}</span>
              <span className="references-tab__preview-size">
                {compressedFile ? (
                  <>
                    <s>{formatBytes(pendingFile.size)}</s> → {formatBytes(compressedFile.size)} (
                    {Math.round((1 - compressedFile.size / pendingFile.size) * 100)}% smaller, as{" "}
                    {compressedFile.type === "image/webp" ? "WebP" : "JPEG"})
                  </>
                ) : (
                  formatBytes(pendingFile.size)
                )}
              </span>
              {compressedFile?.type === "image/webp" ? (
                <span className="references-tab__preview-note">
                  WebP keeps transparency with real compression, unlike PNG — but isn't explicitly
                  confirmed supported by every ad platform's HTML5 creative pipeline.
                </span>
              ) : null}
              {compressError ? <span className="references-tab__preview-error">{compressError}</span> : null}
            </div>
            <div className="references-tab__preview-actions">
              {canCompress ? (
                <button
                  type="button"
                  onClick={handleCompress}
                  disabled={isCompressing || isUploading}
                  className="references-tab__preview-btn"
                >
                  {isCompressing ? "Compressing…" : "Compress"}
                </button>
              ) : null}
              <button
                type="button"
                onClick={handleConfirmUpload}
                disabled={isUploading}
                className="references-tab__preview-btn references-tab__preview-btn--primary"
              >
                {isUploading ? "Uploading…" : "Upload"}
              </button>
              <button
                type="button"
                onClick={handleCancelPending}
                disabled={isUploading}
                className="references-tab__preview-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : null}

        <input
          ref={fileInputRef}
          type="file"
          accept={kind === "font" ? ".woff2" : "image/*"}
          onChange={handleFileSelected}
          className="references-tab__file-input"
        />

        {atLimit ? (
          <p className="references-tab__error">
            This brand is at its {LIMITS.MAX_REFERENCE_IMAGES_PER_BRAND}-image limit. Delete a
            reference to upload another.
          </p>
        ) : null}
      </div>

      {/* Filter */}
      <div className="references-tab__filters" role="group" aria-label="Filter by kind">
        {["all", ...KINDS.map((k) => k.value)].map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => setFilter(value)}
            aria-pressed={filter === value}
            className={`references-tab__filter-chip${
              filter === value ? " references-tab__filter-chip--active" : ""
            }`}
          >
            {value}
            {value !== "all" ? (
              <span className="references-tab__filter-count">
                {items.filter((item) => item.kind === value).length}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {deleteError ? <p className="references-tab__error">{deleteError}</p> : null}

      {/* Grid */}
      {visible.length === 0 ? (
        <p className="references-tab__empty">
          {items.length === 0
            ? "No references yet. Upload a logo to start — it becomes the brand thumbnail."
            : `No ${filter} references yet.`}
        </p>
      ) : (
        <ul className="references-tab__grid">
          {visible.map((item) => (
            <li key={item.id} className="reference-card">
              <div className="reference-card__preview">
                {item.kind === "font" ? (
                  <span className="reference-card__font-glyph" aria-label="Font file">
                    Aa
                  </span>
                ) : (
                  <img src={item.url} alt={item.label || `${item.kind} reference`} />
                )}
              </div>
              <div className="reference-card__meta">
                <div className="reference-card__text">
                  <p className="reference-card__label">{item.label || "Untitled"}</p>
                  <p className="reference-card__kind">
                    {item.kind}
                    {referenceSizeLevel(item.sizeBytes) ? (
                      <span
                        className={`reference-card__size reference-card__size--${referenceSizeLevel(item.sizeBytes)}`}
                        title={
                          referenceSizeLevel(item.sizeBytes) === "hard"
                            ? `Over ${Math.round(SIZE_WARN_HARD_BYTES / 1024)} KB — consider a lighter file`
                            : referenceSizeLevel(item.sizeBytes) === "soft"
                              ? `Over ${Math.round(SIZE_WARN_SOFT_BYTES / 1024)} KB — worth a lighter file if used often`
                              : undefined
                        }
                      >
                        {Math.round(item.sizeBytes / 1024)} KB
                      </span>
                    ) : null}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleDelete(item)}
                  disabled={pendingDeleteId === item.id || !editable}
                  aria-label={`Delete ${item.label || item.kind} reference`}
                  className="reference-card__delete"
                >
                  <TrashIcon className="references-tab__icon" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}